import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Sparkles, Gift, Trophy, ArrowRight, Clock } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

/* /promo/{slug} — public landing page for an active promotion.
 *
 * If the user is signed in we send them to the wallet (where the credit will
 * appear once it's awarded). Otherwise we send them to /signup with the
 * promo slug stored in localStorage so we can announce the reward post-signup.
 */
export default function PromoLanding() {
  const { slug } = useParams();
  const { user } = useAuth();
  const [promo, setPromo] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get(`/promotions/by-slug/${slug}`)
      .then((r) => setPromo(r.data))
      .catch((e) => setErr(e.response?.status === 404 ? "Promotion not found" : e.response?.data?.detail || "Promotion ended"));
  }, [slug]);

  // Stash the slug so signup/login can claim later
  useEffect(() => {
    if (slug) {
      try { localStorage.setItem("tapintv:promo_slug", slug); } catch {}
    }
  }, [slug]);

  if (err) {
    return (
      <div data-testid="promo-error" className="min-h-screen flex items-center justify-center px-4 pt-24 text-center">
        <div>
          <Sparkles className="w-10 h-10 text-white/30 mx-auto" />
          <div className="font-display font-black text-2xl mt-3">{err}</div>
          <Link to="/" className="mt-4 inline-block bg-tv-accent text-black font-bold px-5 py-2.5 rounded-full">Back to Tap IN TV</Link>
        </div>
      </div>
    );
  }
  if (!promo) {
    return <div className="min-h-screen flex items-center justify-center pt-24 text-white/50">Loading…</div>;
  }

  const reward = [];
  if (promo.reward_cents) reward.push(`$${(promo.reward_cents / 100).toFixed(2)}`);
  if (promo.reward_points) reward.push(`${promo.reward_points.toLocaleString()} points`);

  return (
    <div data-testid="promo-landing" className="min-h-screen pt-20 pb-24 text-white relative overflow-hidden">
      <div aria-hidden className="absolute inset-0 -z-10">
        <div className="absolute inset-0 bg-gradient-to-br from-[#1a0c2e] via-[#3b0764] to-[#0b0420]" />
        <div className="absolute inset-0 opacity-50" style={{
          backgroundImage: "radial-gradient(circle at 20% 20%, rgba(236,72,153,0.6), transparent 45%), radial-gradient(circle at 80% 70%, rgba(34,211,238,0.5), transparent 50%)",
        }} />
      </div>
      <main className="max-w-3xl mx-auto px-4 sm:px-6 text-center pt-12">
        <div className="inline-flex items-center gap-2 bg-tv-accent text-black font-black uppercase tracking-[0.25em] text-[10px] px-3 py-1 rounded-full mb-5">
          <Gift className="w-3.5 h-3.5" /> Limited-time promo
        </div>
        <h1 data-testid="promo-headline" className="font-display font-black text-5xl sm:text-6xl md:text-7xl tracking-tighter leading-[1.02]">
          {promo.landing_headline}
        </h1>
        {promo.landing_subhead && (
          <p data-testid="promo-subhead" className="mt-5 text-white/80 text-base sm:text-lg max-w-2xl mx-auto">{promo.landing_subhead}</p>
        )}

        {/* Reward callout */}
        <div data-testid="promo-reward" className="mt-8 inline-flex flex-wrap items-center justify-center gap-3 bg-black/40 backdrop-blur ring-1 ring-white/15 rounded-2xl px-5 py-4">
          <Trophy className="w-7 h-7 text-tv-accent" />
          <div className="text-left">
            <div className="text-[10px] uppercase tracking-[0.25em] text-tv-accent font-black">You'll get</div>
            <div className="font-display font-black text-2xl sm:text-3xl">{reward.join(" + ") || "An exclusive perk"}</div>
          </div>
        </div>

        {/* CTA */}
        <div className="mt-10 flex items-center justify-center gap-3 flex-wrap">
          {user ? (
            <Link
              to="/wallet"
              data-testid="promo-cta-wallet"
              className="bg-tv-accent hover:bg-yellow-400 text-black font-black px-6 py-3 rounded-full inline-flex items-center gap-2 shadow-2xl"
            >See my wallet <ArrowRight className="w-4 h-4" /></Link>
          ) : (
            <>
              <Link
                to={`/register?promo=${encodeURIComponent(promo.slug || "")}`}
                data-testid="promo-cta-signup"
                className="bg-tv-accent hover:bg-yellow-400 text-black font-black px-6 py-3 rounded-full inline-flex items-center gap-2 shadow-2xl"
              >Claim my reward <ArrowRight className="w-4 h-4" /></Link>
              <Link to="/login" data-testid="promo-cta-login" className="bg-white/10 hover:bg-white/20 text-white font-bold px-6 py-3 rounded-full">Already have an account?</Link>
            </>
          )}
        </div>

        {promo.ends_at && (
          <div className="mt-6 text-white/50 text-xs inline-flex items-center gap-1.5">
            <Clock className="w-3.5 h-3.5" /> Ends {new Date(promo.ends_at).toLocaleString()}
          </div>
        )}

        <div className="mt-12 text-[10px] uppercase tracking-[0.3em] text-white/35">Tap IN TV Network · Promotion Terms apply</div>
      </main>
    </div>
  );
}
